// @ts-nocheck

// Wait for Algolia and Instantsearch libraries to load
function waitForInstantsearch() {
  return new Promise((resolve) => {
    if (window.instantsearch && window.algoliasearch && window.ALGOLIA_CONFIG) {
      resolve();
    } else {
      const checkInterval = setInterval(() => {
        if (window.instantsearch && window.algoliasearch && window.ALGOLIA_CONFIG) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    }
  });
}

/**
 * Remove no-results class from search results page when hits are found
 * @param {string} sectionType - The type of section (products, blog, recipes, pages, popular)
 */
function handleSearchResults(sectionType) {
  const searchResultsPage = document.querySelector('.search-results-page');
  if (searchResultsPage && searchResultsPage.classList.contains('no-results')) {
    // For popular section, always keep no-results class
    if (sectionType === 'popular') {
      return;
    }

    // For other sections, remove no-results class when hits are found
    searchResultsPage.classList.remove('no-results');
  }
}

/**
 * @typedef {Object} AlgoliaSearchResultRefs
 * @property {HTMLElement} searchResultsPage - The main search results page container
 */

/**
 * @extends {Component<AlgoliaSearchResultRefs>}
 */
class AlgoliaSearchResult extends HTMLElement {
  constructor() {
    super();
    this.searchClient = null;
    this.search = null;
    this._datalayerOnCount = null;
  }

  connectedCallback() {
    this.showMoreText = this.getAttribute('show-more-text') || 'Show more';
    this.loadMoreText = this.getAttribute('load-more-text') || 'Load more';
    this.showTimeToReadText = this.getAttribute('time-to-read-text') || '{0} to read';
    this.blogCategory = this.getAttribute('blog-category') || 'Articles,News';
    this.recipeCategory = this.getAttribute('recipe-category') || 'Recipes';
    this.productsPerPage = parseInt(this.getAttribute('products-per-page')) || 24;
    this.recipesPerPage = parseInt(this.getAttribute('recipes-per-page')) || 6;
    this.blogPerPage = parseInt(this.getAttribute('blog-per-page')) || 6;
    this.pagesPerPage = parseInt(this.getAttribute('pages-per-page')) || 6;
    this.showLoadMoreNumber = this.getAttribute('show-load-more-number') === 'true';
    this.#initializeSearch();
  }

  disconnectedCallback() {
    if (this.search) {
      this.search.dispose();
    }
  }

  async #initializeSearch() {
    await waitForInstantsearch();

    // Check if Algolia is enabled and configured
    if (typeof window.ALGOLIA_CONFIG === 'undefined' || !window.ALGOLIA_CONFIG) {
      console.warn('Algolia search is not configured');
      return;
    }

    const config = window.ALGOLIA_CONFIG;

    // Get search parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q') || '';

    if (!searchQuery) {
      return;
    }

    // Initialize Algolia search client
    this.searchClient = algoliasearch(config.appId, config.apiKey);

    // Query all metaobject type badge
    await this.#loadBadges();

    // Search all indices by default
    const indicesToSearch = [
      { name: config.productsSearchIndexName, type: 'products' },
      { name: config.blogSearchIndexName, type: 'blog' },
      { name: config.recipeSearchIndexName, type: 'recipes' },
      { name: config.pageSearchIndexName, type: 'pages' },
      { name: config.querySuggestionsIndexName, type: 'popular' }
    ];

    // Create InstantSearch instance
    this.search = instantsearch({
      indexName: indicesToSearch[0].name,
      searchClient: this.searchClient,
    });
    // Add widgets for each index (pass callback so templates use closure, avoid this-binding issues)
    const datalayerOnCount = this.#createDatalayerSearchCallback(searchQuery);
    this._datalayerOnCount = datalayerOnCount;
    indicesToSearch.forEach((indexConfig) => {
      this.#addIndexWidget(indexConfig, searchQuery, datalayerOnCount);
    });

    // Start the search
    this.search.start();

    // Setup smooth scrolling for jump to section links
    this.#setupSmoothScrolling();
  }

  /** Stats callback: accumulate nbHits, debounce, push once. Only push when we have received data. */
  #createDatalayerSearchCallback(searchQuery) {
    const c = { products: 0, blog: 0, recipes: 0, pages: 0 };
    let pushed = false;
    let hasData = false;
    let debounceId = null;
    let fallbackId = null;
    const push = () => {
      if (pushed || !hasData) return;
      pushed = true;
      if (debounceId != null) clearTimeout(debounceId);
      if (fallbackId != null) clearTimeout(fallbackId);
      const total = c.products + c.blog + c.recipes + c.pages;
      const dl = window.dataLayer || [];
      window.dataLayer = dl;
      const payload = { event: 'search', search_term: String(searchQuery || '').trim(), results_count: total };
      const u = document.querySelector('[data-datalayer-search-url]')?.getAttribute('data-datalayer-search-url')?.trim();
      if (u) payload.url = u;
      dl.push({ ecommerce: null });
      dl.push(payload);
    };
    const onCount = (type, n) => {
      if (type in c) {
        hasData = true;
        c[type] = Math.max(0, parseInt(n, 10) || 0);
        clearTimeout(debounceId);
        debounceId = setTimeout(push, 400);
      }
    };
    fallbackId = setTimeout(push, 5000);
    return onCount;
  }

  #handleNoResults() {
    const allSections = document.querySelectorAll('.search-results-section:not(#popular)');

    for (const section of allSections) {
      const sectionNoResults = section.querySelector('.search-no-results');
      if (sectionNoResults) {
        return true;
      }
    }
    const noResultFoundText = document.querySelector('.no-search-results-found');
    noResultFoundText?.classList.remove('hidden');
    return false;
  }

  #addIndexWidget(indexConfig, searchQuery, datalayerOnCount) {
    if (indexConfig.type === 'products') {
      this.#addProductsWidget(indexConfig, searchQuery, datalayerOnCount);
    } else if (indexConfig.type === 'blog') {
      this.#addBlogWidget(indexConfig, searchQuery, datalayerOnCount);
    } else if (indexConfig.type === 'recipes') {
      this.#addRecipesWidget(indexConfig, searchQuery, datalayerOnCount);
    } else if (indexConfig.type === 'pages') {
      this.#addPagesWidget(indexConfig, searchQuery, datalayerOnCount);
    } else if (indexConfig.type === 'popular') {
      this.#addPopularWidget(indexConfig, searchQuery);
    }
  }

  #addProductsWidget(indexConfig, searchQuery, datalayerOnCount) {
    // Check if container exists
    const container = document.querySelector('#products-hits');
    if (!container) {
      return;
    }

    // Check if stats container exists
    const statsContainer = document.querySelector('#products-count');
    const isStatusEnable = !!statsContainer;

    const widgets = [
      instantsearch.widgets.configure({
        query: searchQuery,
        hitsPerPage: this.productsPerPage,
      }),
      instantsearch.widgets.infiniteHits({
        container: '#products-hits',
        templates: {
          item: (hit) => {
            handleSearchResults('products');
            return this.#renderProductCard(hit);
          },
          empty: () => {
            this.#handleNoResults();
            return '<p class="search-no-results"></p>';
          },
          showMoreText: () => {
            return this.loadMoreText;
          },
          showPreviousText: () => {
            return this.showPreviousText;
          }
        },
        showPrevious: false,
        showMore: true
      })
    ];

    // Only add stats widget if container is available
    if (isStatusEnable) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#products-count',
          templates: {
            text: (data) => {
              datalayerOnCount?.('products', data.nbHits);
              if (data.nbHits === 0) {
                return `<span class="search-no-results"></span>`;
              }
              return `(${data.nbHits})`;
            }
          }
        })
      );
    }

    // Add stats widget to update Load More button text (only if enabled)
    if (this.showLoadMoreNumber) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#products-load-more',
          templates: {
            text: (data) => {
              const totalHits = data.nbHits;
              const loadedItems = document.querySelectorAll('#products-hits .ais-InfiniteHits-list .ais-InfiniteHits-item').length;
              const remaining = totalHits - loadedItems;

              const loadMoreButton = document.querySelector('#products-hits .ais-InfiniteHits-loadMore');
              if (loadMoreButton) {
                if (remaining > 0) {
                  loadMoreButton.textContent = `${this.loadMoreText} (${remaining})`;
                } else {
                  loadMoreButton.textContent = this.loadMoreText;
                }
              }
              return ''; // Don't render anything in the stats container
            }
          }
        })
      );
    }

    this.search.addWidgets([
      instantsearch.widgets.index({ indexName: indexConfig.name })
        .addWidgets(widgets)
    ]);
  }

  #addBlogWidget(indexConfig, searchQuery, datalayerOnCount) {
    // Check if container exists
    const container = document.querySelector('#blog-hits');
    if (!container) {
      return;
    }

    // Check if stats container exists
    const statsContainer = document.querySelector('#blog-count');
    const isStatusEnable = !!statsContainer;

    const widgets = [
      instantsearch.widgets.configure({
        query: searchQuery,
        hitsPerPage: this.blogPerPage,
        facetFilters: [['blog.title:Articles', 'blog.title:News']]
      }),
      instantsearch.widgets.infiniteHits({
        container: '#blog-hits',
        templates: {
          item: (hit) => {
            handleSearchResults('blog');
            return this.#renderBlogCard(hit);
          },
          empty: () => {
            this.#handleNoResults();
            return '<p class="search-no-results"></p>';
          },
          showMoreText: () => {
            return this.loadMoreText;
          },
          showPreviousText: () => {
            return this.showPreviousText;
          }
        },
        showPrevious: false,
        showMore: true
      })
    ];

    // Only add stats widget if container is available
    if (isStatusEnable) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#blog-count',
          templates: {
            text: (data) => {
              datalayerOnCount?.('blog', data.nbHits);
              if (data.nbHits === 0) {
                return `<span class="search-no-results"></span>`;
              }
              return `(${data.nbHits})`;
            }
          }
        })
      );
    }

    // Add stats widget to update Load More button text (only if enabled)
    if (this.showLoadMoreNumber) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#blog-load-more',
          templates: {
            text: (data) => {
              const totalHits = data.nbHits;
              const loadedItems = document.querySelectorAll('#blog-hits .ais-InfiniteHits-list .ais-InfiniteHits-item').length;
              const remaining = totalHits - loadedItems;

              const loadMoreButton = document.querySelector('#blog-hits .ais-InfiniteHits-loadMore');
              if (loadMoreButton) {
                if (remaining > 0) {
                  loadMoreButton.textContent = `${this.loadMoreText} (${remaining})`;
                } else {
                  loadMoreButton.textContent = this.loadMoreText;
                }
              }
              return ''; // Don't render anything in the stats container
            }
          }
        })
      );
    }

    this.search.addWidgets([
      instantsearch.widgets.index({ indexName: indexConfig.name })
        .addWidgets(widgets)
    ]);
  }

  #addRecipesWidget(indexConfig, searchQuery, datalayerOnCount) {
    // Check if container exists
    const container = document.querySelector('#recipes-hits');
    if (!container) {
      return;
    }

    // Check if stats container exists
    const statsContainer = document.querySelector('#recipes-count');
    const isStatusEnable = !!statsContainer;

    const widgets = [
      instantsearch.widgets.configure({
        query: searchQuery,
        hitsPerPage: this.recipesPerPage,
        facetFilters: ['blog.title:Recipes']
      }),
      instantsearch.widgets.infiniteHits({
        container: '#recipes-hits',
        templates: {
          item: (hit) => {
            handleSearchResults('recipes');
            return this.#renderRecipeCard(hit);
          },
          empty: () => {
            this.#handleNoResults();
            return '<p class="search-no-results"></p>';
          },
          showMoreText: () => {
            return this.loadMoreText;
          },
          showPreviousText: () => {
            return this.showPreviousText;
          }
        },
        showPrevious: false,
        showMore: true
      })
    ];

    // Only add stats widget if container is available
    if (isStatusEnable) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#recipes-count',
          templates: {
            text: (data) => {
              datalayerOnCount?.('recipes', data.nbHits);
              if (data.nbHits === 0) {
                return `<span class="search-no-results"></span>`;
              }
              return `(${data.nbHits})`;
            }
          }
        })
      );
    }

    // Add stats widget to update Load More button text (only if enabled)
    if (this.showLoadMoreNumber) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#recipes-load-more',
          templates: {
            text: (data) => {
              const totalHits = data.nbHits;
              const loadedItems = document.querySelectorAll('#recipes-hits .ais-InfiniteHits-list .ais-InfiniteHits-item').length;
              const remaining = totalHits - loadedItems;

              const loadMoreButton = document.querySelector('#recipes-hits .ais-InfiniteHits-loadMore');
              if (loadMoreButton) {
                if (remaining > 0) {
                  loadMoreButton.textContent = `${this.loadMoreText} (${remaining})`;
                } else {
                  loadMoreButton.textContent = this.loadMoreText;
                }
              }
              return ''; // Don't render anything in the stats container
            }
          }
        })
      );
    }

    this.search.addWidgets([
      instantsearch.widgets.index({ indexName: indexConfig.name })
        .addWidgets(widgets)
    ]);
  }

  #addPagesWidget(indexConfig, searchQuery, datalayerOnCount) {
    // Check if container exists
    const container = document.querySelector('#pages-hits');
    if (!container) {
      return;
    }

    // Check if stats container exists
    const statsContainer = document.querySelector('#pages-count');
    const isStatusEnable = !!statsContainer;

    const widgets = [
      instantsearch.widgets.configure({
        query: searchQuery,
        hitsPerPage: this.pagesPerPage,
      }),
      instantsearch.widgets.infiniteHits({
        container: '#pages-hits',
        templates: {
          item: (hit) => {
            handleSearchResults('pages');
            return this.#renderPageCard(hit);
          },
          empty: () => {
            this.#handleNoResults();
            return '<p class="search-no-results"></p>';
          },
          showMoreText: () => {
            return this.loadMoreText;
          },
          showPreviousText: () => {
            return this.showPreviousText;
          }
        },
        showPrevious: false,
        showMore: true
      })
    ];

    // Only add stats widget if container is available
    if (isStatusEnable) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#pages-count',
          templates: {
            text: (data) => {
              datalayerOnCount?.('pages', data.nbHits);
              if (data.nbHits === 0) {
                return `<span class="search-no-results"></span>`;
              }
              return `(${data.nbHits})`;
            }
          }
        })
      );
    }

    // Add stats widget to update Load More button text (only if enabled)
    if (this.showLoadMoreNumber) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#pages-load-more',
          templates: {
            text: (data) => {
              const totalHits = data.nbHits;
              const loadedItems = document.querySelectorAll('#pages-hits .ais-InfiniteHits-list .ais-InfiniteHits-item').length;
              const remaining = totalHits - loadedItems;

              const loadMoreButton = document.querySelector('#pages-hits .ais-InfiniteHits-loadMore');
              if (loadMoreButton) {
                if (remaining > 0) {
                  loadMoreButton.textContent = `${this.loadMoreText} (${remaining})`;
                } else {
                  loadMoreButton.textContent = this.loadMoreText;
                }
              }
              return ''; // Don't render anything in the stats container
            }
          }
        })
      );
    }

    this.search.addWidgets([
      instantsearch.widgets.index({ indexName: indexConfig.name })
        .addWidgets(widgets)
    ]);
  }

  #addPopularWidget(indexConfig, searchQuery) {
    // Check if container exists
    const container = document.querySelector('#popular-hits');
    if (!container) {
      return;
    }

    // Check if stats container exists
    const statsContainer = document.querySelector('#popular-count');
    const isStatusEnable = !!statsContainer;

    const widgets = [
      instantsearch.widgets.hits({
        container: '#popular-hits',
        templates: {
          item: (hit) => {
            handleSearchResults('popular');
            return this.#renderPopularCard(hit);
          },
          empty: () => {
            return '<p class="search-no-results"></p>';
          }
        }
      })
    ];

    // Only add stats widget if container is available
    if (isStatusEnable) {
      widgets.push(
        instantsearch.widgets.stats({
          container: '#popular-count',
          templates: {
            text: (data) => {
              if (data.nbHits === 0) {
                return `<span class="search-no-results"></span>`;
              }
              return `(${data.nbHits})`;
            }
          }
        })
      );
    }

    this.search.addWidgets([
      instantsearch.widgets.index({ indexName: indexConfig.name })
        .addWidgets(widgets)
    ]);
  }

  async #loadBadges() {
    try {
      // Get API configuration from theme settings
      const apiVersion = window.Shopify?.theme?.settings?.storefront_api_version || '2025-10';

      // Query all metaobject type badge
      const response = await fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.theme.settings?.storefront_access_token || ''
        },
        body: JSON.stringify({
          query: `
            query {
              metaobjects(type: "badge", first: 100) {
                edges {
                  node {
                    id
                    handle
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
          `
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.badges = data.data?.metaobjects?.edges?.map(edge => {
          const badge = { id: edge.node.id, handle: edge.node.handle };
          edge.node.fields.forEach(field => {
            badge[field.key] = field.value;
          });
          return badge;
        }) || [];
      } else {
        console.warn('Failed to load badges:', response.status);
        this.badges = [];
      }
    } catch (error) {
      console.warn('Error loading badges:', error);
      this.badges = [];
    }
  }

  #setupSmoothScrolling() {
    document.querySelectorAll('.jump-to-section__link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          // Get navigation height for offset
          const navigation = document.querySelector('.search-results-navigation');
          const offset = navigation ? navigation.offsetHeight : 0;

          // Calculate target position with offset
          const targetPosition = targetElement.offsetTop - offset;

          // Smooth scroll to position
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  #renderBadges(badgesData) {
    if (!badgesData || !Array.isArray(badgesData)) {
      return '';
    }

    // Sort badges by priority (higher priority first)
    const sortedBadges = badgesData.sort((a, b) => (b.priority || 0) - (a.priority || 0));

    return `
      <ul class="product-labels-outer top-left">
        ${sortedBadges.map((badge, index) => `
          <li class="product-badge-${index} product-label"
              aria-live="polite"
              style="background-color: ${badge.background || '#000000'};
                     color: ${badge.color || '#ffffff'};
                     order: ${badge.priority || 0};
                     border-radius: 16px;">
            ${badge.name || 'Badge'}
          </li>
        `).join('')}
      </ul>
    `;
  }

  #renderProductCard(hit) {
    // Extract product data
    const title = hit.title || 'Untitled Product';
    const image = hit.image || hit.product_image || '/assets/no-image.svg';
    const handle = hit.handle || '';
    const url = hit.url || `/products/${handle}`;

    // Parse badge IDs from hit meta data and map to full badge objects
    let badges = [];
    if (hit?.meta?.custom?.amb_badges) {
      try {
        const badgeIds = JSON.parse(hit.meta.custom.amb_badges);
        if (Array.isArray(badgeIds) && this.badges) {
          // Map badge IDs to full badge objects
          badges = badgeIds
            .map(badgeId => this.badges.find(badge => badge.id === badgeId))
            .filter(badge => badge); // Remove undefined entries
        }
      } catch (error) {
        console.warn('Failed to parse badges:', error);
        badges = [];
      }
    }

    const badgesHtml = this.#renderBadges(badges);

    // Build product card HTML - enhanced design with background texture and badges
    return `
      <a href="${url}" class="algolia-product-item algolia-product-item__link">
        <div class="algolia-product-item__image-container">
          <div class="algolia-product-item__image-wrapper">
            <img src="${image}" alt="${title}" loading="lazy">
          </div>
        </div>
        ${badgesHtml}
        <div class="algolia-product-item__content">
          <h3 class="algolia-product-item__title">
            ${title}
          </h3>
        </div>
      </a>
    `;
  }

  #renderRecipeCard(hit) {
    // Extract recipe data
    const title = hit.title || 'Untitled Recipe';
    const image = hit.image || hit.featured_image || '/assets/no-image.svg';
    const handle = hit.handle || '';
    const url = hit.url || `/blogs/${hit.blog?.handle || 'recipes'}/${handle}`;
    const tags = hit.tags || [];

    // Build tags HTML - limit to first 3 tags for display
    const displayTags = tags.slice(0, 3);
    const tagsHtml = displayTags.map(tag =>
      `<span class="algolia-content-tag">${tag}</span>`
    ).join('');

    // Build recipe card HTML - clean design with tags overlay
    return `
    <article class="algolia-content-card ">
      <a href="${url}" class="algolia-content-card__link-overlay" aria-hidden="true" tabindex="-1"></a>
      <div class="algolia-content-card__image-container">
        <div class="algolia-content-card__image-wrapper">
          <img src="${image}" alt="${title}" loading="lazy" class="algolia-content-card__image">
        </div>
        <div class="algolia-content-card__tags">
          ${tagsHtml}
        </div>
      </div>
      <div class="algolia-content-card__content">
        <h3 class="algolia-hit-card__title">
          <span class="algolia-recipe-card__title-text">${title}</span>
          <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.168 15.9997H21.5013M21.5013 15.9997L16.8346 11.333M21.5013 15.9997L16.8346 20.6663" stroke="#C1003B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </h3>
      </div>
    </article>
  `;
  }

  #renderBlogCard(hit) {
    // Extract blog data
    const title = hit.title || 'Untitled Article';
    const image = hit.image || hit.featured_image || '/assets/no-image.svg';
    const handle = hit.handle || '';
    const url = hit.url || `/blogs/${hit.blog?.handle || 'articles'}/${handle}`;
    const tags = hit.tags || [];
    const publishedAt = hit.published_at || hit.created_at;

    // Format date
    let formattedDate = '';
    if (publishedAt) {
      const date = new Date(publishedAt);
      formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }

    // Calculate read time (rough estimate: 200 words per minute)
    const wordCount = hit.body_html ? hit.body_html.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));

    // Build tags HTML - limit to first 2 tags for display
    const displayTags = tags.slice(0, 2);
    const tagsHtml = displayTags.map(tag =>
      `<span class="algolia-content-tag">${tag}</span>`
    ).join('');

    // Get translation attributes
    const timeToReadTextTemplate = this.showTimeToReadText || '{0} mins read';
    const readMoreText = this.readMoreText || 'Read more';
    const timeToReadText = timeToReadTextTemplate.replace('{0}', readTime);

    // Build blog card HTML - clean design with tags overlay
    return `
    <article class="algolia-content-card ">
      <a href="${url}" class="algolia-content-card__link-overlay" aria-hidden="true" tabindex="-1"></a>
      <div class="algolia-content-card__image-container">
        <div class="algolia-content-card__image-wrapper">
          <img src="${image}" alt="${title}" loading="lazy" class="algolia-content-card__image">
        </div>
        <div class="algolia-content-card__tags">
          ${tagsHtml}
        </div>
      </div>
      <div class="algolia-content-card__content">
        <div class="algolia-content-card__meta">
          ${formattedDate} • ${timeToReadText}
        </div>
        <h3 class="algolia-hit-card__title"> ${title} </h3>
      </div>
    </article>
  `;
  }

  #renderPageCard(hit) {
    // Extract page data
    const title = hit.title || 'Untitled Page';
    const handle = hit.handle || '';
    const url = hit.url || `/pages/${handle}`;

    // Build page card HTML - clean list design with full-line link
    return `
     <div class="algolia-page-card">
       <a href="${url}" class="algolia-page-card__link">
         <h3 class="algolia-page-card__title">${title}</h3>
         <span class="algolia-page-card__read-more">${this.showMoreText}</span>
       </a>
     </div>
   `;
  }

  #renderPopularCard(hit) {
    // Extract query suggestion data
    const query = hit.query || hit.suggestion || 'Search term';
    const url = `${window.location.pathname}?q=${encodeURIComponent(query)}`;

    // Build popular search tag HTML - pill-shaped tag
    return `
     <a href="${url}" class="algolia-popular-tag">
       ${query}
     </a>
   `;
  }
}

// Register the component
customElements.define('algolia-search-result', AlgoliaSearchResult);
